import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * "Fresh registration welcome — 14-day trial auto-offer" sticky banner.
 * Shows only when the business is Free + hasn't used a trial + either fresh
 * from signup (?welcome=1 in URL) or the banner hasn't been dismissed yet.
 */
export default function TrialWelcomeBanner({ biz, onActivated }) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(`mc68_trial_welcome_${biz?.slug}`) === "1"
  );
  const isFreeAndEligible = (biz?.premium?.tier || "free") === "free" && !biz?.trial_used;
  const isFreshWelcome = params.get("welcome") === "1";
  const showBanner = isFreeAndEligible && (isFreshWelcome || !dismissed);
  if (!biz || !showBanner) return null;

  const firstName = biz?.claimed_by_first_name || "";
  const heading = (t.trial?.welcome_h1 || "Bienvenue {name}, démarrez votre essai gratuit 14 jours")
    .replace("{name}", firstName ? firstName + "," : "")
    .replace("  ", " ");

  const startTrial = async () => {
    setBusy(true);
    try {
      await api.post("/dashboard/trial/start", { business_slug: biz.slug });
      toast.success(t.trial?.welcome_cta || "Essai activé");
      localStorage.setItem(`mc68_trial_welcome_${biz.slug}`, "1");
      params.delete("welcome");
      setParams(params, { replace: true });
      onActivated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(`mc68_trial_welcome_${biz.slug}`, "1");
    params.delete("welcome");
    setParams(params, { replace: true });
    setDismissed(true);
  };

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-primary to-[#0f3d29] text-primary-foreground rounded-sm p-6 md:p-7 mb-6"
      data-testid="trial-welcome-banner"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-secondary/30 rounded-full blur-3xl" />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div className="flex-1">
          <div className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-sm mb-3">
            {t.trial?.welcome_kicker || "OFFRE DE BIENVENUE · SANS CARTE BANCAIRE"}
          </div>
          <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-2" data-testid="trial-welcome-heading">
            {heading}
          </h3>
          <p className="text-sm opacity-90 leading-relaxed max-w-xl">
            {t.trial?.welcome_sub || "Débloquez toutes les fonctionnalités Premium AI. Aucun engagement, aucune carte demandée."}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2 flex-wrap">
          <button
            onClick={startTrial}
            disabled={busy}
            className="bg-white text-primary hover:bg-[#F9F8F6] px-5 py-3 rounded-sm font-semibold text-sm shadow-lg disabled:opacity-60 flex items-center gap-2"
            data-testid="trial-welcome-cta"
          >
            {busy ? "…" : (t.trial?.welcome_cta || "Activer mon essai 14 jours")}
          </button>
          <button
            onClick={dismiss}
            className="text-xs opacity-60 hover:opacity-100 px-2 py-1"
            data-testid="trial-welcome-skip"
          >
            {t.trial?.welcome_skip || "Plus tard"}
          </button>
        </div>
      </div>
    </div>
  );
}
